import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Modal, Table, Tag,
} from "antd";
import moment from "moment";
import { toast } from "react-toastify";
import { fetchRcaTracking } from "../../../actions/qc";
import useAbortController from "../../Common/hooks/useAbortController";
import MlRoutingFlagReasonCell from "../Dashboard/MLRouting/MlRoutingFlagReasonCell";
import { formatMlRoutingFlagsAndReasons } from "../Dashboard/MLRouting/mlRoutingUtils";
import "../Dashboard/MLRouting/DashboardMlRouting.css";
import AddRcaSubmitModal from "../DataOpsAnalytics/AddRcaSubmitModal";
import { formatAddRcaTableRcaStatus } from "../DataOpsAnalytics/utils/addRcaTestSamplesUtils";
import {
  buildRcaSubmitRowFromTrackingRecord,
  enrichRcaRowWithMockFields,
  getRcaStatusTagColor,
  resolveRcaAssigneeDisplay,
} from "../DataOpsAnalytics/utils/rcaUtils";
import RcaDocuments from "./RcaDocuments";
import "../ReportDelay/ReportDelayFormPopup.css";
import "./RcaTracking/RcaTracking.css";

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
};

const formatDisplayDateTime = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY, h:mm a") : value;
};

const getStatusTagColor = (rcaStatus) => {
  if (rcaStatus === 1) return "processing";
  if (rcaStatus === 2) return "success";
  return "default";
};

const getStatusLabel = (rcaStatus) => {
  if (rcaStatus === 1) return "Active";
  if (rcaStatus === 2) return "Completed";
  return "Unknown";
};

const buildDetailColumns = ({
  isDataOpsVariant,
  onEditRecord,
}) => {
  const columns = [
    {
      title: "Status",
      dataIndex: "rca_status",
      key: "rca_status",
      render: (rcaStatus, record) => {
        if (isDataOpsVariant) {
          const label = formatAddRcaTableRcaStatus(rcaStatus, record);
          if (label === "-") {
            return label;
          }
          return (
            <Tag color={getRcaStatusTagColor(rcaStatus, record)}>
              {label}
            </Tag>
          );
        }
        return (
          <Tag color={getStatusTagColor(rcaStatus)}>{getStatusLabel(rcaStatus)}</Tag>
        );
      },
    },
    ...(isDataOpsVariant ? [{
      title: "Assign To",
      dataIndex: "assigned_to",
      key: "assigned_to",
      width: 180,
      render: (_value, record) => {
        const label = resolveRcaAssigneeDisplay(record);
        return label && String(label).trim() !== "" ? label : "-";
      },
    }] : []),
    {
      title: "Expected",
      dataIndex: "expected_complete_date",
      key: "expected_complete_date",
      render: formatDisplayDate,
    },
    {
      title: "Completed",
      dataIndex: "actual_complete_date",
      key: "actual_complete_date",
      render: formatDisplayDate,
    },
    {
      title: "Updated at",
      dataIndex: "updated_at",
      key: "updated_at",
      render: formatDisplayDateTime,
    },
    {
      title: "Updated By",
      dataIndex: "updated_by_name",
      key: "updated_by_name",
      render: (value) => value || "-",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDisplayDateTime,
    },
    {
      title: "Created By",
      dataIndex: "created_by_name",
      key: "created_by_name",
      render: (value) => value || "-",
    },
    ...(isDataOpsVariant && onEditRecord ? [{
      title: "Actions",
      key: "actions",
      width: 96,
      align: "center",
      onHeaderCell: () => ({ className: "ReportDelayFormPopup__ActionsColumn" }),
      onCell: () => ({ className: "ReportDelayFormPopup__ActionsColumn" }),
      render: (_value, record) => (
        <Button
          type="link"
          size="small"
          className="ReportDelayFormPopup__RowEditAction"
          onClick={(event) => {
            event.stopPropagation();
            onEditRecord(record);
          }}
        >
          Edit
        </Button>
      ),
    }] : []),
  ];
  return columns;
};

/**
 * Same RCA history detail modal used on the RCA Tracking tab.
 * Loads all RCA records for the given tis_pk.
 */
const RcaHistoryModal = ({
  open,
  onCancel,
  tisPk,
  workorderId,
  genistaId,
  analysis,
  variant = "qc",
  parentRecord = null,
  onRcaDetailsUpdated,
}) => {
  const isDataOpsVariant = variant === "dataOps";
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSubmitRow, setEditSubmitRow] = useState(null);
  const { setNewAbortController } = useAbortController();

  useEffect(() => {
    if (!open || !tisPk) {
      return undefined;
    }

    let canceled = false;
    const loadDetail = async () => {
      setDetailLoading(true);
      setDetailRows([]);
      const abortController = setNewAbortController();
      const {
        success, result, message, canceled: requestCanceled,
      } = await fetchRcaTracking({
        tis_pk: tisPk,
        filter: "ALL",
      }, abortController.signal);

      canceled = requestCanceled;
      if (canceled) {
        return;
      }

      if (success) {
        const mappedRows = (Array.isArray(result) ? result : []).map((item) => (
          enrichRcaRowWithMockFields({ ...item, key: item.id ?? item.rca_id })
        ));
        setDetailRows(mappedRows);
      } else {
        toast.error(message);
        setDetailRows([]);
      }
      setDetailLoading(false);
    };

    loadDetail();
    return () => {
      setNewAbortController();
    };
  }, [open, tisPk]); // eslint-disable-line

  const handleCancel = () => {
    setNewAbortController();
    setDetailRows([]);
    onCancel();
  };

  const reloadDetailRows = useCallback(async () => {
    if (!tisPk) {
      return;
    }
    setDetailLoading(true);
    const abortController = setNewAbortController();
    const {
      success, result, message,
    } = await fetchRcaTracking({
      tis_pk: tisPk,
      filter: "ALL",
    }, abortController.signal);
    if (success) {
      const mappedRows = (Array.isArray(result) ? result : []).map((item) => (
        enrichRcaRowWithMockFields({ ...item, key: item.id ?? item.rca_id })
      ));
      setDetailRows(mappedRows);
    } else {
      toast.error(message);
    }
    setDetailLoading(false);
  }, [tisPk, setNewAbortController]);

  const handleEditRecord = useCallback((record) => {
    const context = {
      ...(parentRecord || {}),
      tis_pk: tisPk,
      wo_id_pk: parentRecord?.wo_id_pk,
      workorder_id: workorderId ?? parentRecord?.workorder_id,
      genista_id: genistaId ?? parentRecord?.genista_id,
      analysis: analysis ?? parentRecord?.analysis ?? parentRecord?.test_name,
      test_name: analysis ?? parentRecord?.test_name ?? parentRecord?.analysis,
      test_category: parentRecord?.category_name
        ?? parentRecord?.test_category
        ?? parentRecord?.test_category_name,
      customer_name: parentRecord?.customer_company
        ?? parentRecord?.customer_name
        ?? parentRecord?.customer_id,
      sample_id: parentRecord?.sample_id,
    };
    setEditSubmitRow(buildRcaSubmitRowFromTrackingRecord(record, context));
    setEditModalOpen(true);
  }, [tisPk, workorderId, genistaId, analysis, parentRecord]);

  const detailColumns = buildDetailColumns({
    isDataOpsVariant,
    onEditRecord: isDataOpsVariant ? handleEditRecord : undefined,
  });

  const renderHistoryDocuments = (record) => {
    const mlReasonPairs = formatMlRoutingFlagsAndReasons(record?.ml_reasons);
    return (
      <>
        {isDataOpsVariant && mlReasonPairs.length > 0 && (
          <div className="RcaTracking__DetailMlReason">
            <strong>ML Reason</strong>
            <MlRoutingFlagReasonCell pairs={mlReasonPairs} />
          </div>
        )}
        <div className="RcaTracking__ExpandedDocuments">
          <RcaDocuments
            s3Keys={Array.isArray(record.s3_keys) ? record.s3_keys : []}
            emptyText="No documents available"
          />
        </div>
      </>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      width={900}
      title="RCA"
      className="RcaTracking__DetailModal"
      destroyOnClose
    >
      <div className="RcaTracking__DetailMeta">
        <div>
          <strong>WO:</strong>
          {" "}
          {workorderId || "-"}
        </div>
        <div>
          <strong>Genista ID:</strong>
          {" "}
          {genistaId || "-"}
        </div>
        <div>
          <strong>Analysis:</strong>
          {" "}
          {analysis || "-"}
        </div>
      </div>

      <div className="RcaTracking__DetailSection">
        <Table
          size="small"
          loading={detailLoading}
          pagination={false}
          rowKey="id"
          dataSource={detailRows}
          columns={detailColumns}
          expandable={{
            expandedRowRender: renderHistoryDocuments,
            rowExpandable: () => true,
          }}
        />
      </div>

      <AddRcaSubmitModal
        open={editModalOpen}
        selectedRows={editSubmitRow ? [editSubmitRow] : []}
        subtitleContext={{
          ...(parentRecord || {}),
          workorder_id: workorderId ?? parentRecord?.workorder_id,
          genista_id: genistaId ?? parentRecord?.genista_id,
          test_name: analysis ?? parentRecord?.test_name ?? parentRecord?.analysis,
          analysis: analysis ?? parentRecord?.analysis ?? parentRecord?.test_name,
          test_category: parentRecord?.category_name
            ?? parentRecord?.test_category
            ?? parentRecord?.test_category_name,
        }}
        onCancel={() => {
          setEditModalOpen(false);
          setEditSubmitRow(null);
        }}
        onSubmitted={() => {
          setEditModalOpen(false);
          setEditSubmitRow(null);
          reloadDetailRows();
          if (onRcaDetailsUpdated) {
            onRcaDetailsUpdated();
          }
        }}
      />
    </Modal>
  );
};

export default RcaHistoryModal;
